import React, { useState, useEffect } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Divider, FormControlLabel, Grid, MenuItem, Paper, Radio, RadioGroup, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, IconButton } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { API } from "../../api/api";
import { END_POINTS } from "../../api/endpoints";
import { useSelector, useDispatch } from "react-redux";
import * as prescriptionAction from "../../redux/actions/prescriptionActions";
import { useToast } from "../../context/ToastContext";

const PreconceptionLeapPanel = ({ patientId, consultationId }) => {
    const showToast = useToast();
    const dispatch = useDispatch();
    const patientReducer = useSelector((state) => state.patientReducer);
    const patientConditions = patientReducer?.patientInfo?.conditions || [];

    const [loading, setLoading] = useState(true);
    const [leapState, setLeapState] = useState(null);
    const [weekPack, setWeekPack] = useState(null);
    const [assessmentResult, setAssessmentResult] = useState(null);
    const [partnerData, setPartnerData] = useState(null);
    const [artData, setArtData] = useState(null);
    const [t1Data, setT1Data] = useState(null);
    const [gate2Status, setGate2Status] = useState("");
    const [gate3Outcome, setGate3Outcome] = useState("not_yet");
    const [gate4Selection, setGate4Selection] = useState("");

    const isConditionMatched = patientConditions.some(c => c.code === "PRECONCEPTION" || c.name?.toUpperCase().includes("PRECONCEPTION"));

    useEffect(() => {
        if (!patientId) return;
        fetchLeapState();
    }, [patientId]);

    const fetchLeapState = async () => {
        try {
            setLoading(true);
            const res = await API.get(`/v1/patients/${patientId}/preconception`);
            if (res.success || res.data) {
                const data = res.data || res;
                setLeapState(data);

                if (data?.enrolled) {
                    if (data?.enrollment?.state === 'ART_ACTIVE' || data?.art) {
                        fetchArtCycles();
                    } else if (data?.enrollment?.state !== 'COMPLETE') {
                        fetchWeekPack();
                    }
                }
            }
        } catch (e) {
            console.error("Failed to fetch LEAP state", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchWeekPack = async () => {
        try {
            const res = await API.get(`/v1/patients/${patientId}/preconception/week?role=HER`);
            if (res.success || res.data) setWeekPack(res.data);
        } catch (e) { }
    };

    const fetchArtCycles = async () => {
        try {
            const res = await API.get(END_POINTS.preconceptionArtCycles(patientId));
            if (res.success || res.data) setArtData(res.data);
        } catch (e) { }
    };

    const fetchT1Data = async () => {
        try {
            const res = await API.get(END_POINTS.preconceptionT1);
            if (res.success || res.data) setT1Data(res.data);
        } catch (e) { }
    };

    const handleAssess = async () => {
        try {
            const res = await API.post(`/v1/patients/${patientId}/preconception/assess`, { consultationId });
            if (res.success || res.data) {
                showToast("Assessment complete", "success");
                setAssessmentResult(res.data);
            } else if (res.errorCode === "PRECONCEPTION_GATE1_INCOMPLETE") {
                showToast("Please complete Age, TTC Duration, and Conceived fields in Observation first", "error");
            } else {
                showToast(res.message || "Assessment failed", "error");
            }
        } catch (e) {
            showToast("Error performing assessment", "error");
        }
    };

    const handleEnroll = async (mode = "NATURAL") => {
        try {
            const res = await API.post(`/v1/patients/${patientId}/preconception/enroll`, { consultationId, mode });
            if (res.success || res.data) {
                showToast("Enrolled successfully", "success");
                fetchLeapState();
                setAssessmentResult(null);
            } else {
                showToast(res.message || "Failed to enroll", "error");
            }
        } catch (e) {
            showToast("Error enrolling patient", "error");
        }
    };

    const handleAdvanceWeek = async () => {
        try {
            const res = await API.post(`/v1/patients/${patientId}/preconception/week/advance`, {});
            if (res.success || res.data) {
                showToast("Week advanced", "success");
                fetchLeapState();
            } else if (res.errorCode === "PRECONCEPTION_WEEK_REGRESS") {
                showToast("Cannot regress week", "error");
            } else {
                showToast(res.message || "Failed to advance week", "error");
            }
        } catch (e) {
            showToast("Error advancing week", "error");
        }
    };

    const handleLinkPartner = async () => {
        try {
            const res = await API.post(`/v1/patients/${patientId}/preconception/partner/link`, {});
            if (res.success || res.data) setPartnerData(res.data);
        } catch (e) { }
    };

    const handleUnlinkPartner = async () => {
        try {
            await API.post(`/v1/patients/${patientId}/preconception/partner/unlink`, {});
            showToast("Partner unlinked", "success");
            setPartnerData(null);
            fetchLeapState();
        } catch (e) { }
    };

    const handleGate2 = async () => {
        if (!gate2Status) return showToast("Select Week 8 Status", "error");
        try {
            const res = await API.post(END_POINTS.preconceptionGate2(patientId), { week8Status: gate2Status, notes: "" });
            if (res.success || res.data) {
                showToast("Gate 2 evaluated", "success");
                fetchLeapState();
            } else if (res.errorCode === "PRECONCEPTION_GATE2_INCOMPLETE") {
                showToast("Please save Week 8 status in Observation first", "error");
            }
        } catch (e) { }
    };

    const handleGate3 = async () => {
        try {
            const res = await API.post(END_POINTS.preconceptionGate3(patientId), { outcome: gate3Outcome, cause: "unexplained" });
            if (res.success || res.data) {
                showToast("Gate 3 evaluated", "success");
                fetchLeapState();
            } else if (res.errorCode === "PRECONCEPTION_GATE3_TOO_EARLY") {
                showToast("Too early for Gate 3", "error");
            }
        } catch (e) { }
    };

    const handleGate4Decision = async (modality) => {
        try {
            const res = await API.post(END_POINTS.preconceptionGate4(patientId), { modality, savedFrom: "treatment_tab" });
            if (res.success || res.data) {
                showToast(`Gate 4 Decision saved: ${modality}`, "success");
                fetchLeapState();
                setAssessmentResult(null);
            }
        } catch (e) { }
    };

    const handleComplete = async () => {
        try {
            const res = await API.post(END_POINTS.preconceptionComplete(patientId), {});
            if (res.success || res.data) {
                showToast("Programme completed", "success");
                fetchLeapState();
            }
        } catch (e) { }
    };

    const handleArtPhaseChange = async (cycleId, nextPhase) => {
        try {
            const res = await API.post(END_POINTS.preconceptionArtPhase(patientId, cycleId), { phaseKey: nextPhase });
            if (res.success || res.data) {
                showToast("ART Phase updated", "success");
                fetchArtCycles();
            }
        } catch (e) { }
    };

    const handleProtocolStatusChange = async (rowId, newStatus) => {
        try {
            const res = await API.patch(END_POINTS.preconceptionArtProtocol(patientId, artData.active.cycle.id), {
                rowId,
                status: newStatus,
                consultationId
            });
            if (res.success || res.data) {
                const updatedActive = res.data || res;
                setArtData(prev => ({ ...prev, active: updatedActive }));
                dispatch(prescriptionAction.getRecommendedPrescriptionRequestAction(consultationId, "SUPPLEMENT"));
                window.dispatchEvent(new CustomEvent('force_supplement_refresh'));
            } else {
                showToast(res.message || "Failed to update protocol", "error");
            }
        } catch (e) {
            showToast("Error updating protocol", "error");
        }
    };

    const handleProtocolReset = async (rowId) => {
        try {
            const res = await API.patch(END_POINTS.preconceptionArtProtocol(patientId, artData.active.cycle.id), {
                rowId,
                reset: true
            });
            if (res.success || res.data) {
                const updatedActive = res.data || res;
                setArtData(prev => ({ ...prev, active: updatedActive }));
            } else {
                showToast(res.message || "Failed to reset protocol", "error");
            }
        } catch (e) {
            showToast("Error resetting protocol", "error");
        }
    };

    const handleT1ProtocolStatusChange = async (rowId, newStatus) => {
        try {
            const res = await API.patch(END_POINTS.preconceptionT1Protocol(patientId), {
                rowId,
                status: newStatus,
                consultationId
            });
            if (res.success || res.data) {
                const updatedT1 = res.data || res;
                setLeapState(prev => ({ ...prev, t1: updatedT1 }));
                if (t1Data) setT1Data(updatedT1);
                showToast("T1 Protocol updated", "success");
                dispatch(prescriptionAction.getRecommendedPrescriptionRequestAction(consultationId, "SUPPLEMENT"));
                window.dispatchEvent(new CustomEvent('force_supplement_refresh'));
            } else {
                showToast(res.message || "Failed to update T1 protocol", "error");
            }
        } catch (e) {
            showToast("Error updating T1 protocol", "error");
        }
    };

    const handleT1ProtocolReset = async (rowId) => {
        try {
            const res = await API.patch(END_POINTS.preconceptionT1Protocol(patientId), {
                rowId,
                reset: true
            });
            if (res.success || res.data) {
                const updatedT1 = res.data || res;
                setLeapState(prev => ({ ...prev, t1: updatedT1 }));
                if (t1Data) setT1Data(updatedT1);
                showToast("T1 Protocol reset", "success");
            } else {
                showToast(res.message || "Failed to reset T1 protocol", "error");
            }
        } catch (e) {
            showToast("Error resetting T1 protocol", "error");
        }
    };

    const renderT1ProtocolTable = (protocolList) => (
        <>
            <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Supplement</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '160px' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pregnancy Dose</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {protocolList?.map((p, i) => (
                            <TableRow key={p.id || i}>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    {p.supplement}
                                    {p.isOverridden && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <Chip label="Overridden" size="small" color="warning" sx={{ height: '20px', fontSize: '10px' }} />
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => handleT1ProtocolReset(p.id)}
                                                sx={{ textTransform: 'none', ml: 1, p: 0, minWidth: 'auto', fontSize: '12px' }}
                                            >
                                                Reset
                                            </Button>
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={p.status || ''}
                                        onChange={(e) => handleT1ProtocolStatusChange(p.id, e.target.value)}
                                        sx={{
                                            '& .MuiSelect-select': { py: 0.5, fontSize: '13px' }
                                        }}
                                    >
                                        {(p.statusOptions || ["CONTINUE", "INCREASE", "MODIFY", "STOP", "DISCONTINUE"]).map((opt) => (
                                            <MenuItem key={opt} value={opt} sx={{ fontSize: '13px' }}>
                                                {opt.charAt(0) + opt.slice(1).toLowerCase()}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: p.status === 'DISCONTINUE' || p.status === 'STOP' ? '#DC2626' : '#059669' }}>
                                    {p.actionsByStatus?.[p.status] ?? p.action}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px' }}>
                                    {p.pregnancyDose || '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                {protocolList?.map((p, i) => (
                    <Box key={p.id || i} sx={{ mb: 1.5, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                {p.supplement}
                            </Typography>
                            {p.isOverridden && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Chip label="Overridden" size="small" color="warning" sx={{ height: '20px', fontSize: '10px' }} />
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleT1ProtocolReset(p.id)}
                                        sx={{ textTransform: 'none', ml: 1, p: 0, minWidth: 'auto', fontSize: '12px' }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', minWidth: '90px' }}>Status:</Typography>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    value={p.status || ''}
                                    onChange={(e) => handleT1ProtocolStatusChange(p.id, e.target.value)}
                                    sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '13px' } }}
                                >
                                    {(p.statusOptions || ["CONTINUE", "INCREASE", "MODIFY", "STOP", "DISCONTINUE"]).map((opt) => (
                                        <MenuItem key={opt} value={opt} sx={{ fontSize: '13px' }}>
                                            {opt.charAt(0) + opt.slice(1).toLowerCase()}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', minWidth: '90px', mt: 0.2 }}>Action:</Typography>
                                <Typography sx={{ fontSize: '13px', color: p.status === 'DISCONTINUE' || p.status === 'STOP' ? '#DC2626' : '#059669' }}>
                                    {p.actionsByStatus?.[p.status] ?? p.action}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', minWidth: '90px', mt: 0.2 }}>Pregnancy Dose:</Typography>
                                <Typography sx={{ fontSize: '13px' }}>
                                    {p.pregnancyDose || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );

    const formatText = (text) => {
        return text.toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    const handleArtComplete = async (cycleId, outcome) => {
        try {
            const res = await API.post(END_POINTS.preconceptionArtComplete(patientId, cycleId), { outcome });
            if (res.success || res.data) {
                showToast("ART Cycle completed", "success");
                if (outcome === 'PREGNANT') {
                    fetchT1Data();
                }
                fetchArtCycles();
                fetchLeapState();
            }
        } catch (e) { }
    };

    if (loading) return null;
    const isVisible = isConditionMatched || leapState?.enrolled;
    if (!isVisible) return null;

    return (
        <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
            <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid #E6E8EC', borderRadius: 3, '&:before': { display: 'none' } }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="preconception-leap-content"
                    id="preconception-leap-header"
                    sx={{
                        px: 2,
                        '& .MuiAccordionSummary-content': { m: 0 },
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography fontWeight="bold" sx={{ fontSize: { xl: '19px', lg: '16px', md: '14px' }, textAlign: 'left' }}>
                            Preconception LEAP Programme
                        </Typography>
                        {leapState?.enrolled && (
                            <Chip label={`Week ${leapState.enrollment?.week || 1} • ${leapState.enrollment?.state}`} color="primary" variant="outlined" size="small" sx={{ mr: 2 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, pt: 0 }}>
                    {/* 1. NOT ENROLLED */}
                    {!leapState?.enrolled && !assessmentResult && (
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontSize: '13px', color: '#666', mb: 0.5 }}>
                                Patient is not yet enrolled in the Preconception LEAP programme. Assess eligibility and start.
                            </Typography>
                            <Button variant="outlined" onClick={handleAssess} size="small" sx={{ textTransform: 'none' }}>
                                Assess Eligibility (Gate 1)
                            </Button>
                        </Box>
                    )}

                    {/* 2. GATE 1 ASSESSMENT RESULT */}
                    {!leapState?.enrolled && assessmentResult && (
                        <Box sx={{
                            borderRadius: '12px',
                            border: '1px solid #F4D19F',
                            overflow: 'hidden',
                            textAlign: 'left',
                        }}>
                            {/* Header Banner */}
                            {assessmentResult.urgencyLabel && (
                                <Box sx={{
                                    backgroundColor: '#FCE6C1',
                                    px: 1,
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <ReportProblemOutlinedIcon sx={{ color: '#874B04', fontSize: '20px' }} />
                                    <Typography sx={{ color: '#874B04', fontWeight: 600, fontSize: '15px' }}>
                                        {assessmentResult.urgencyLabel}
                                    </Typography>
                                </Box>
                            )}

                            {/* Main Content */}
                            <Box sx={{ p: 1, backgroundColor: '#FFF' }}>
                                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#111827', }}>
                                    {assessmentResult.headline}
                                </Typography>

                                {assessmentResult.summary && (
                                    <Typography sx={{ fontSize: '15px', color: '#4B5563', mb: 1, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                        {assessmentResult.summary}
                                    </Typography>
                                )}

                                {assessmentResult.actions && assessmentResult.actions.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 1, borderColor: '#F3F4F6' }} />
                                        <Box component="ul" sx={{ m: 0, pl: 3, mt: 1, listStyleType: 'circle', '& li': { mb: 1.5, color: '#111827', fontSize: '15px', pl: 1, '&::marker': { fontSize: '18px' } } }}>
                                            {assessmentResult.actions.map((act, i) => (
                                                <li key={i}>{act.text}</li>
                                            ))}
                                        </Box>
                                    </>
                                )}

                                {assessmentResult.canEnroll && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleEnroll(assessmentResult.suggestedMode || 'NATURAL')}
                                        fullWidth
                                        sx={{
                                            mt: 3,
                                            py: 1.2,
                                            borderColor: '#E5E7EB',
                                            color: '#111827',
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '15px',
                                            '&:hover': {
                                                borderColor: '#D1D5DB',
                                                backgroundColor: '#F9FAFB'
                                            }
                                        }}
                                    >
                                        Start Programme ({assessmentResult.suggestedMode})
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* 3. ENROLLED ACTIVE (Week Pack & Gates) */}
                    {leapState?.enrolled && leapState?.enrollment?.state !== 'ART_ACTIVE' && leapState?.enrollment?.state !== 'COMPLETE' && !t1Data && (
                        <Box>
                            {weekPack && (
                                <Box p={1} sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', textAlign: 'left', mb: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                        <Box>
                                            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                                                Week {weekPack.programmeWeek} pack: {weekPack.pack?.theme?.toLowerCase()}
                                            </Typography>
                                            <Typography sx={{ fontSize: '15px', color: '#111827', mt: 0.5 }}>
                                                <b>Primary nutrient:</b> {weekPack.pack?.primaryNutrient?.toLowerCase()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {weekPack.testsDue?.length > 0 && (
                                        <>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase', mb: 1 }}>
                                                Due:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                                                {weekPack.testsDue.map((test, index) => {
                                                    let firstPart = test;
                                                    let secondPart = "";
                                                    let separator = "";
                                                    if (test.includes(":")) {
                                                        const parts = test.split(":");
                                                        firstPart = parts[0];
                                                        separator = " — ";
                                                        secondPart = parts.slice(1).join(":");
                                                    } else if (test.includes(" — ")) {
                                                        const parts = test.split(" — ");
                                                        firstPart = parts[0];
                                                        separator = " — ";
                                                        secondPart = parts.slice(1).join(" — ");
                                                    } else if (test.includes(" - ")) {
                                                        const parts = test.split(" - ");
                                                        firstPart = parts[0];
                                                        separator = " — ";
                                                        secondPart = parts.slice(1).join(" - ");
                                                    }

                                                    return (
                                                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                            <CheckCircleOutlineIcon sx={{ color: '#059669', fontSize: '20px', mt: 0.2 }} />
                                                            <Typography sx={{ fontSize: '15px', color: '#111827', lineHeight: 1.5 }}>
                                                                {secondPart ? (
                                                                    <>
                                                                        <b>{firstPart}</b>{separator}{secondPart}
                                                                    </>
                                                                ) : (
                                                                    test
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </>
                                    )}
                                    {/* <Button variant="contained" size="small" onClick={handleAdvanceWeek} sx={{ mt: 1, textTransform: 'none', backgroundColor: '#10B981', '&:hover': { backgroundColor: '#059669' }, mb: 1 }}>
                                        Advance Week
                                    </Button> */}
                                </Box>
                            )}

                            {/* Gate 2 */}
                            {(leapState.enrollment?.week >= 8 && leapState.enrollment?.week < 12) && (
                                // summary after evaluation of gate2
                                leapState?.gate2 ? (
                                    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 1, backgroundColor: '#FFFFFF', textAlign: 'left' }}>
                                        {leapState.gate2.urgencyLabel && (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#FCE8E8', borderRadius: '50px', px: 1.5, py: 0.5, mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent', mr: 1 }}>
                                                    <ReportProblemOutlinedIcon sx={{ color: '#991B1B', fontSize: '18px' }} />
                                                </Box>
                                                <Typography sx={{ color: '#991B1B', fontWeight: 600, fontSize: '12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                                    {leapState.gate2.urgencyLabel}
                                                </Typography>
                                            </Box>
                                        )}
                                        {leapState.gate2.headline && (
                                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827', fontFamily: 'serif', mb: 1, lineHeight: 1.3 }}>
                                                {leapState.gate2.headline}
                                            </Typography>
                                        )}
                                        {leapState.gate2.summary && (
                                            <Typography sx={{ fontSize: '14px', color: '#4B5563', mb: 1, lineHeight: 1.5 }}>
                                                {leapState.gate2.summary}
                                            </Typography>
                                        )}

                                        {leapState.gate2.actions && leapState.gate2.actions.length > 0 && (
                                            <>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                                                    {leapState.gate2.actions.map((act, i) => {
                                                        let icon = <ArrowForwardIcon sx={{ color: '#4B5563', fontSize: '15px' }} />;
                                                        if (act.color === 'red' || act.color === 'orange') {
                                                            icon = <ArrowForwardIcon sx={{ color: act.color === 'red' ? '#991B1B' : '#B45309', fontSize: '15px' }} />;
                                                        } else if (act.color === 'green') {
                                                            icon = <CheckCircleOutlineIcon sx={{ color: '#15803D', fontSize: '15px' }} />;
                                                        } else if (act.text?.toLowerCase().includes('anovulatory')) {
                                                            icon = <ArrowForwardIcon sx={{ color: '#991B1B', fontSize: '15px' }} />;
                                                        } else if (act.text?.toLowerCase().includes('homa')) {
                                                            icon = <ArrowForwardIcon sx={{ color: '#B45309', fontSize: '15px' }} />;
                                                        } else if (act.text?.toLowerCase().includes('leap')) {
                                                            icon = <CheckCircleOutlineIcon sx={{ color: '#15803D', fontSize: '15px' }} />;
                                                        }

                                                        return (
                                                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4 }}>
                                                                <Box sx={{ mt: 0.2, display: 'flex', alignItems: 'center' }}>
                                                                    {icon}
                                                                </Box>
                                                                <Typography sx={{ fontSize: '15px', color: '#111827', lineHeight: 1.5 }}>
                                                                    {act.text || act.name || act.title || JSON.stringify(act)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </>
                                        )}

                                        {!leapState.gate2.headline && !leapState.gate2.summary && (
                                            <Typography sx={{ fontSize: '13px' }}>
                                                {leapState.gate2.week8Status || JSON.stringify(leapState.gate2)}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 1, mb: 2, backgroundColor: '#FFFFFF', textAlign: 'left' }}>
                                        {/* gate 2  */}
                                        <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 0.5, color: '#111827' }}>Gate 2 (Week ≥ 8)</Typography>
                                        <>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1.5, color: '#4B5563' }}>Week 8 status</Typography>

                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                {[
                                                    { value: 'not_yet', label: 'Not yet ovulatory / too early' },
                                                    { value: 'anovulatory_persisting', label: 'Anovulatory Persisting' },
                                                    { value: 'ovulation_confirmed', label: 'Ovulation Confirmed' },
                                                    { value: 'homa_high', label: 'HOMA-IR high' }
                                                ].map((option) => (
                                                    <Chip
                                                        key={option.value}
                                                        label={option.label}
                                                        onClick={() => setGate2Status(option.value)}
                                                        variant="outlined"
                                                        sx={{
                                                            backgroundColor: gate2Status === option.value ? '#FFF5EE' : 'transparent',
                                                            border: gate2Status === option.value ? "1px solid #F56423" : '1px solid #D1D5DB',
                                                            color: gate2Status === option.value ? '#C2410C' : '#374151',
                                                            fontWeight: gate2Status === option.value ? 600 : 400,
                                                            '&:hover': {
                                                                backgroundColor: gate2Status === option.value ? '#FFF5EE' : '#F3F4F6'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleGate2}
                                                disabled={!gate2Status}
                                                sx={{
                                                    textTransform: 'none',
                                                    backgroundColor: '#059669',
                                                    color: '#FFF',
                                                    fontWeight: 500,
                                                    borderRadius: '8px',
                                                    px: 3,
                                                    py: 1,
                                                    boxShadow: 'none',
                                                    '&:hover': { backgroundColor: '#047857', boxShadow: 'none' },
                                                    '&:disabled': { backgroundColor: '#E5E7EB', color: '#9CA3AF' }
                                                }}
                                            >
                                                Evaluate Gate 2
                                            </Button>
                                        </>
                                    </Box>
                                )
                            )}

                            {/* Gate 3 */}
                            {(leapState.enrollment?.week == 12 || leapState.enrollment?.week == 13) && (
                                <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 1, mb: 2, backgroundColor: '#FFFFFF', textAlign: 'left' }}>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 0.5, color: '#111827' }}>Gate 3 (Week ≥ 12)</Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1.5, color: '#4B5563' }}>Outcome</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            label="Not Conceived Yet"
                                            onClick={() => setGate3Outcome('not_yet')}
                                            variant="outlined"
                                            sx={{
                                                backgroundColor: gate3Outcome === 'not_yet' ? '#FFF5EE' : 'transparent',
                                                border: gate3Outcome === 'not_yet' ? "1px solid #F56423" : '1px solid #D1D5DB',
                                                color: gate3Outcome === 'not_yet' ? '#C2410C' : '#374151',
                                                fontWeight: gate3Outcome === 'not_yet' ? 600 : 400,
                                                '&:hover': {
                                                    backgroundColor: gate3Outcome === 'not_yet' ? '#FFF5EE' : '#F3F4F6'
                                                }
                                            }}
                                        />
                                        <Chip
                                            label="Conceived"
                                            onClick={() => setGate3Outcome('conceived')}
                                            variant="outlined"
                                            sx={{
                                                backgroundColor: gate3Outcome === 'conceived' ? '#FFF5EE' : 'transparent',
                                                border: gate3Outcome === 'conceived' ? "1px solid #F56423" : '1px solid #D1D5DB',
                                                color: gate3Outcome === 'conceived' ? '#C2410C' : '#374151',
                                                fontWeight: gate3Outcome === 'conceived' ? 600 : 400,
                                                '&:hover': {
                                                    backgroundColor: gate3Outcome === 'conceived' ? '#FFF5EE' : '#F3F4F6'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Button
                                        variant='outlined'
                                        size="small"
                                        onClick={handleGate3}
                                        disabled={!gate3Outcome}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                            px: 3,
                                            py: 1,
                                        }}
                                    >
                                        Evaluate Gate 3
                                    </Button>
                                </Box>
                            )}

                            {/* <Box mt={3} pt={2} borderTop="1px dashed #E5E7EB">
                            <Button variant="text" color="error" size="small" onClick={handleComplete} sx={{ textTransform: 'none' }}>Complete Programme</Button>
                            <Button variant="text" color="primary" size="small" onClick={() => handleGate4Decision('IUI')} sx={{ textTransform: 'none', ml: 2 }}>Proceed to IUI (Gate 4)</Button>
                        </Box> */}
                        </Box>
                    )}

                    {/* 4. COMPLETE STATE */}
                    {leapState?.enrolled && leapState?.t1 && !t1Data && (
                        <Box sx={{ borderRadius: 2, textAlign: 'left', }}>
                            <Box p={1} sx={{ bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', textAlign: 'left' }}>
                                <Typography variant="h6" sx={{ fontSize: '16px', color: '#166534', fontWeight: 'bold' }}>Programme Completed</Typography>
                                <Typography sx={{ fontSize: '13px', mt: 1, color: '#15803D' }}>The patient has completed the LEAP Preconception programme.</Typography>
                                <Typography sx={{ fontSize: '14px', mt: 1, color: '#065F46',  }}>{leapState?.t1?.intro}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '14px', mt: 1, }}>Supplement Protocol Updates:</Typography>
                            {renderT1ProtocolTable(leapState?.t1.supplementProtocol)}


                        </Box>
                    )}

                    {/* show gate 4 */}
                    {(leapState?.enrolled && !artData?.active && !t1Data && leapState?.gate3?.escalateToGate4) && // 
                        <Box display="flex" flexDirection="column" gap={1} sx={{ textAlign: 'left', }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Gate 4 pathway
                            </Typography>
                            <RadioGroup
                                row
                                value={gate4Selection}
                                onChange={(e) => setGate4Selection(e.target.value)}
                            >
                                <FormControlLabel value="IUI" control={<Radio size="small" />} label="IUI" />
                                <FormControlLabel value="IVF" control={<Radio size="small" />} label="IVF" />
                                <FormControlLabel value="OI" control={<Radio size="small" />} label="OI" />
                            </RadioGroup>
                            <Button
                                variant='outlined'
                                onClick={() => handleGate4Decision(gate4Selection)}
                                size="small"
                                disabled={!gate4Selection}
                                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                            >
                                Save pathway
                            </Button>
                        </Box>
                    }

                    {/* 5. ART ACTIVE */}
                    {leapState?.enrolled && artData && !t1Data && artData.active && ( // leapState?.enrollment?.state === 'ART_ACTIVE' &&
                        <Box p={2} sx={{ bgcolor: '#EFF6FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                            <Typography sx={{ fontSize: '16px', color: '#1E40AF', fontWeight: 'bold', textAlign: 'left' }}>Active ART Cycle: {artData.active?.modality}</Typography>
                            {(artData.active?.protocol?.phases || artData.protocol?.phases)?.length > 0 ? (
                                <Box mt={2}>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {(artData.active?.protocol?.phases || artData.protocol?.phases).map(phase => {
                                            const isNext = artData.active.nextPhaseKeys?.includes(phase.key);
                                            const isCurrent = artData.active.phaseKey === phase.key;
                                            return (
                                                <Chip
                                                    key={phase.key}
                                                    label={artData.active?.modality == "IUI" ? phase.key : formatText(phase.key)}
                                                    onClick={isNext ? () => handleArtPhaseChange(artData.active.cycle.id, phase.key) : undefined}
                                                    sx={{ cursor: isNext ? 'pointer' : 'default', fontWeight: isCurrent ? 'bold' : 'normal', color: 'black', backgroundColor: isCurrent ? '#FFF5EE' : isNext ? 'transparent' : 'transparent', border: isCurrent ? '1px solid red' : isNext ? "2px dashed #F56423" : '1px solid grey' }}
                                                />
                                            );
                                        })}
                                    </Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mt: 2, textAlign: 'left' }}>Instructions:</Typography>

                                    {(() => {
                                        const currentPhase = (artData.active?.protocol?.phases || artData.protocol?.phases).find(p => p.key === artData.active.phaseKey);
                                        if (!currentPhase) return null;

                                        const hasItems = (currentPhase.items && Array.isArray(currentPhase.items) && currentPhase.items.length > 0) || currentPhase.item;
                                        if (!hasItems) return null;

                                        return (
                                            <Box mt={1} p={1.5} border="1px solid #E5E7EB" borderRadius={2} bgcolor="#F9FAFB">
                                                {currentPhase.items && Array.isArray(currentPhase.items) && (
                                                    <Box>
                                                        {currentPhase.items.map((item, i) => {
                                                            // if (typeof item === 'string') {
                                                            //     return (
                                                            //         <Typography key={i} sx={{ fontSize: '13px', color: '#4B5563', lineHeight: 1.4, mb: 0.5 }}>
                                                            //             • {item}
                                                            //         </Typography>
                                                            //     );
                                                            // }
                                                            return (
                                                                <Typography key={i} sx={{ fontSize: '13px', color: item.color || '#4B5563', lineHeight: 1.4, mb: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                                    {item.marker ? <span style={{ fontWeight: 'bold' }}>{item.marker}</span> : <span>•</span>}
                                                                    <span>{item.text || item.name || item.title || JSON.stringify(item)}</span>
                                                                </Typography>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                {currentPhase.item && !Array.isArray(currentPhase.items) && (
                                                    typeof currentPhase.item === 'string' ? (
                                                        <Typography sx={{ fontSize: '13px', color: '#4B5563', lineHeight: 1.4 }}>
                                                            • {currentPhase.item}
                                                        </Typography>
                                                    ) : (
                                                        <Typography sx={{ fontSize: '13px', color: currentPhase.item.color || '#4B5563', lineHeight: 1.4, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                            {currentPhase.item.marker ? <span style={{ fontWeight: 'bold' }}>{currentPhase.item.marker}</span> : <span>•</span>}
                                                            <span>{currentPhase.item.text || currentPhase.item.name || currentPhase.item.title || JSON.stringify(currentPhase.item)}</span>
                                                        </Typography>
                                                    )
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            ) : artData.active?.nextPhaseKeys?.length > 0 && (
                                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                                    {artData.active.nextPhaseKeys.map(phase => (
                                        <Button key={phase} size="small" variant="outlined" onClick={() => handleArtPhaseChange(artData.active.cycle.id, phase)} sx={{ textTransform: 'none' }}>
                                            Move to {phase}
                                        </Button>
                                    ))}
                                </Box>
                            )}

                            {artData?.active?.protocolAdjustments?.length > 0 && (
                                <>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mt: 2, textAlign: 'left' }}>Protocol Adjustments:</Typography>
                                    <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                                        <Table size="small" sx={{ mt: 1 }}>
                                            <TableHead sx={{}}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Supplement / Goal</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {artData.active.protocolAdjustments.map((adj) => (
                                                    <TableRow key={adj.id}>
                                                        <TableCell sx={{ fontSize: '13px' }}>
                                                            {adj.supplementOrTrack}
                                                            {adj.isOverridden && (
                                                                <Chip label="Overridden" size="small" color="warning" sx={{ ml: 1, height: '20px', fontSize: '10px' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '13px' }}>
                                                            <TextField
                                                                select
                                                                size="small"
                                                                fullWidth
                                                                value={adj.status}
                                                                onChange={(e) => handleProtocolStatusChange(adj.id, e.target.value)}
                                                                sx={{
                                                                    '& .MuiSelect-select': { py: 0.5, fontSize: '13px' }
                                                                }}
                                                            >
                                                                {(adj.statusOptions || ["CONTINUE", "INCREASE", "MODIFY", "STOP", "DISCONTINUE"]).map((opt) => (
                                                                    <MenuItem key={opt} value={opt} sx={{ fontSize: '13px' }}>
                                                                        {opt.charAt(0) + opt.slice(1).toLowerCase()}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '13px' }}>
                                                            {adj.actionsByStatus?.[adj.status] ?? adj.action}
                                                            {adj.isOverridden && (
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() => handleProtocolReset(adj.id)}
                                                                    sx={{ textTransform: 'none', ml: 1, p: 0, minWidth: 'auto', fontSize: '12px' }}
                                                                >
                                                                    Reset
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1 }}>
                                        {artData.active.protocolAdjustments.map((adj) => (
                                            <Box key={adj.id} sx={{ mb: 1.5, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                                        {adj.supplementOrTrack}
                                                    </Typography>
                                                    {adj.isOverridden && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Chip label="Overridden" size="small" color="warning" sx={{ height: '20px', fontSize: '10px' }} />
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                onClick={() => handleProtocolReset(adj.id)}
                                                                sx={{ textTransform: 'none', ml: 1, p: 0, minWidth: 'auto', fontSize: '12px' }}
                                                            >
                                                                Reset
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', minWidth: '60px' }}>Status:</Typography>
                                                        <TextField
                                                            select
                                                            size="small"
                                                            fullWidth
                                                            value={adj.status}
                                                            onChange={(e) => handleProtocolStatusChange(adj.id, e.target.value)}
                                                            sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '13px' } }}
                                                        >
                                                            {(adj.statusOptions || ["CONTINUE", "INCREASE", "MODIFY", "STOP", "DISCONTINUE"]).map((opt) => (
                                                                <MenuItem key={opt} value={opt} sx={{ fontSize: '13px' }}>
                                                                    {opt.charAt(0) + opt.slice(1).toLowerCase()}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '12px', color: '#6B7280', minWidth: '60px', mt: 0.2 }}>Action:</Typography>
                                                        <Typography sx={{ fontSize: '13px' }}>
                                                            {adj.actionsByStatus?.[adj.status] ?? adj.action}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}

                            {artData.active?.modality == "IUI" && <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mt: 2, textAlign: 'left' }}>IUI Cycle #{(artData?.cycles || []).filter(c => c.modality === 'IUI').length}: MONITORING</Typography>}


                            {(() => {
                                const phases = artData.active?.protocol?.phases || artData.protocol?.phases;
                                const isLastPhase = phases?.length > 0
                                    ? phases[phases.length - 1].key === artData.active?.phaseKey
                                    : (!artData.active?.nextPhaseKeys || artData.active.nextPhaseKeys.length === 0);

                                if (!isLastPhase) return null;
                                return (
                                    <Box mt={1.5} display="flex" gap={2}>
                                        <Button size="small" variant="contained" onClick={() => handleArtComplete(artData.active.cycle.id, 'PREGNANT')} sx={{ textTransform: 'none', bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}>Mark Pregnant (T1)</Button>
                                        <Button size="small" variant="outlined" color="error" onClick={() => handleArtComplete(artData.active.cycle.id, 'NOT_PREGNANT')} sx={{ textTransform: 'none' }}>Mark Not Pregnant</Button>
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}

                    {/* 6. T1 TRANSITION */}
                    {t1Data && (
                        <Box sx={{ borderRadius: 2, textAlign: 'left' }}>
                            <Box p={2} sx={{ bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', textAlign: 'left' }}>
                                <Typography variant="h6" sx={{ fontSize: '18px', color: '#047857', fontWeight: 'bold' }}>T1 Transition (Conceived)</Typography>
                                <Typography sx={{ fontSize: '14px', mt: 1, color: '#065F46', mb: 2 }}>{t1Data.intro}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '14px', mt: 1, }}>Supplement Protocol Updates:</Typography>

                            {renderT1ProtocolTable(t1Data.supplementProtocol)}
                        </Box>
                    )}

                    {/* PARTNER MANAGEMENT */}
                    {leapState?.enrolled && (
                        <Box mt={1} pt={1} sx={{ textAlign: 'left' }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}>
                                Partner Status: {leapState.partner?.linked ? <span style={{ color: '#10B981' }}>Linked</span> : <span style={{ color: '#6B7280' }}>Not Linked</span>}
                            </Typography>

                            {!leapState.partner?.linked ? (
                                <Box>
                                    <Button size="small" variant="outlined" onClick={handleLinkPartner} sx={{ textTransform: 'none', mt: 1 }}>Generate Invite Link</Button>
                                    {partnerData && (
                                        <Box mt={2} p={1.5} bgcolor="#F3F4F6" borderRadius={2} border="1px dashed #D1D5DB">
                                            <Typography sx={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>Invite Code: {partnerData.inviteCode}</Typography>
                                            <Typography sx={{ fontSize: '12px', color: '#4B5563', mt: 0.5 }}>{partnerData.shareText}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                null
                                // <Button size="small" variant="outlined" color="error" onClick={handleUnlinkPartner} sx={{ textTransform: 'none', mt: 1 }}>Unlink Partner</Button>
                            )}
                        </Box>
                    )}

                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

export default PreconceptionLeapPanel;
